/*
Warns support that the account on screen may be one of several belonging to the same
reader — and, when it is, which of them actually carries the subscription.

RDL decides access from the `roles` array while support reads the payment record, so a
reader who registers a second time instead of signing in pays on one account and reads
on another. Both screens then look correct on their own; only seeing them together
explains the "my subscription is active but I get the paywall" ticket.

Renders nothing when there is nothing to say, so it stays out of the way on the ordinary
user. Advisory only: it never merges or edits anything.
*/
import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';

import getUserDuplicates from '../../../api/graphql/queries/getUserDuplicates';
import dismissDuplicatePair from '../../../api/graphql/mutations/dismissDuplicatePair';
import { describeReasons, matchConfidence } from '../../../utils/duplicateAccountLabels';
import formatTimestamp from '../../../utils/formatTimestamp';

const DuplicateAccountsPanel = ({ userId }) => {
  const [duplicates, setDuplicates] = useState(null);
  // Pairs dismissed in this sitting, hidden immediately rather than after a refetch.
  const [dismissedIds, setDismissedIds] = useState([]);

  /* "These are two different people." Without it a wrong pair returns on every nightly
   * rebuild and support learns to ignore the panel. */
  const dismiss = async (pairId) => {
    const result = await dismissDuplicatePair({ pairId, dismissed: true });
    if (result && result.result) setDismissedIds(ids => [...ids, pairId]);
  };

  useEffect(() => {
    if (!userId) return undefined;

    let cancelled = false;
    getUserDuplicates({ userId }).then((result) => {
      if (!cancelled && result && result.isAuth) setDuplicates(result);
    });
    return () => { cancelled = true; };
  }, [userId]);

  if (!duplicates || !duplicates.matches) return null;

  const matches = duplicates.matches.filter(m => dismissedIds.indexOf(m.pairId) < 0);
  if (matches.length === 0) return null;

  // Only worth shouting about while the account it points at is still on screen.
  const stillShowing = matches.some(m => m.holdsSubscription);
  const subscriptionElsewhere = stillShowing ? duplicates.subscriptionElsewhere : null;

  return (
    <div className="mb-4 p-4 bg-gray-900 rounded text-xs">
      {subscriptionElsewhere && (
        <p className="mb-3 p-2 rounded bg-yellow-900 text-yellow-100 font-americaMonoBold uppercase">
          {`La suscripción está en otra cuenta: ${subscriptionElsewhere}`}
        </p>
      )}

      <p className="mb-2 font-americaMonoBold uppercase text-white">
        {`Otras cuentas de esta persona (${matches.length})`}
      </p>

      <ul className="text-white">
        {matches.map(match => (
          <li key={match._id} className="mb-2 pb-2 border-b border-gray-700 last:border-b-0">
            <span className="font-americaMonoBold">{match.email}</span>
            {match.holdsSubscription && (
              <span className="ml-2 px-2 py-px rounded-full bg-green-800 text-green-100">
                {match.subscriptionLastDate
                  ? `Suscrita hasta ${formatTimestamp(match.subscriptionLastDate)}`
                  : 'Suscrita'}
              </span>
            )}
            <span className="block mt-1 text-gray-400">
              {`Alta ${formatTimestamp(match.createdAt)}`}
              {match.socialnetwork ? ` · ${match.socialnetwork}` : ''}
              {` · ${describeReasons(match.reasons).join(', ')}`}
            </span>
            {matchConfidence(match.reasons) === 'low' && (
              <span className="block text-gray-500">
                Solo coincide la dirección: puede ser otra persona del mismo domicilio.
              </span>
            )}
            <button
              type="button"
              className="mt-1 text-gray-400 underline hover:text-white"
              onClick={() => dismiss(match.pairId)}
              title="No volver a mostrar esta pareja"
            >
              No son la misma persona
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

DuplicateAccountsPanel.propTypes = {
  userId: PropTypes.string,
};

DuplicateAccountsPanel.defaultProps = {
  userId: '',
};

export default DuplicateAccountsPanel;
